/**
 * PWA Icon Utilities
 * Dynamically loads site-specific PWA icons based on configuration
 */
import { logWarn, logError } from './logger';

// Cache for icon existence checks to avoid repeated network requests
const iconExistsCache = new Map();

/**
 * Clear the icon existence cache
 * Call this when configuration changes or when debugging
 */
export const clearIconCache = () => {
  iconExistsCache.clear();
};

/**
 * Check if a site-specific icon exists
 * Uses client-side detection with fallback to default
 * @param {string} siteId - Site identifier (e.g., 'nbatime')
 * @param {string} iconName - Icon filename (e.g., 'favicon.ico')
 * @returns {Promise<boolean>} Whether the icon exists
 */
const iconExists = async (siteId, iconName) => {
  const cacheKey = `${siteId}-${iconName}`;

  // Return cached result if available
  if (iconExistsCache.has(cacheKey)) {
    return iconExistsCache.get(cacheKey);
  }

  // Check if icon exists by attempting to load it
  return new Promise((resolve) => {
    const img = new Image();
    const iconUrl = `/icons/${siteId}/${iconName}`;

    let resolved = false;

    img.onload = () => {
      if (!resolved) {
        resolved = true;
        iconExistsCache.set(cacheKey, true);
        resolve(true);
      }
    };

    img.onerror = () => {
      if (!resolved) {
        resolved = true;
        logWarn(`[PWA Icons] Missing icon: ${iconUrl}, using default`);
        iconExistsCache.set(cacheKey, false);
        resolve(false);
      }
    };

    // Start loading the image
    img.src = iconUrl;

    // Set a timeout to avoid hanging - increased timeout for better reliability
    setTimeout(() => {
      if (!resolved) {
        resolved = true;
        logWarn(`[PWA Icons] Timeout checking icon: ${iconUrl}, using default`);
        iconExistsCache.set(cacheKey, false);
        resolve(false);
      }
    }, 3000); // Increased from default to 3 seconds
  });
};

/**
 * Get the site identifier from configuration
 * Uses app_file_name from API response (e.g., "ncaatime" -> "ncaatime")
 * @param {Object} configuration - Site configuration object from /api/siteInfoByUrl
 * @returns {string} Site identifier (e.g., 'nbatime', 'dubtime')
 */
export const getSiteIdentifier = (configuration) => {
  if (!configuration) {
    logWarn('[PWA Icons] No configuration provided for getSiteIdentifier');
    return null;
  }

  // First try app_file_name (most reliable - no transformation needed)
  if (configuration.app_file_name) {
    const siteId = configuration.app_file_name.toLowerCase();
    return siteId;
  }

  // Fallback to site_name transformation (legacy support)
  if (configuration.site_name) {
    // Convert "NBA Time" -> "nbatime", "Dub Time" -> "dubtime"
    // Remove spaces, parentheses, and content in parentheses for cleaner IDs
    let siteId = configuration.site_name.toLowerCase();

    // Remove content in parentheses and the parentheses themselves
    siteId = siteId.replace(/\s*\([^)]*\)\s*/g, '');

    // Remove remaining spaces
    siteId = siteId.replace(/\s+/g, '');

    return siteId;
  }

  logWarn(
    '[PWA Icons] No app_file_name or site_name found in configuration',
    configuration
  );
  return null;
};

/**
 * Get the icon path for a specific site and icon type
 * Uses fallback logic to ensure valid paths
 * @param {string} siteId - Site identifier (e.g., 'dubtime')
 * @param {string} iconName - Icon filename (e.g., 'icon-192.png')
 * @returns {string} Path to icon
 */
export const getIconPath = (siteId, iconName) => {
  if (!siteId) {
    return `/icons/default/${iconName}`;
  }
  const path = `/icons/${siteId}/${iconName}`;
  return path;
};

/**
 * Get the icon path with fallback checking (async version)
 * @param {string} siteId - Site identifier (e.g., 'dubtime')
 * @param {string} iconName - Icon filename (e.g., 'icon-192.png')
 * @returns {Promise<string>} Path to icon (with fallback)
 */
export const getIconPathWithFallback = async (siteId, iconName) => {
  if (!siteId) {
    return `/icons/default/${iconName}`;
  }

  // Check if site-specific icon exists
  const exists = await iconExists(siteId, iconName);
  const finalPath = exists
    ? `/icons/${siteId}/${iconName}`
    : `/icons/default/${iconName}`;

  // Double-check: if we're returning a default path, verify the default exists
  if (!exists) {
    const defaultExists = await iconExists('default', iconName);
    if (!defaultExists) {
      logWarn(
        `[PWA Icons] CRITICAL: Default icon missing: /icons/default/${iconName}`
      );
    }
  }

  return finalPath;
};

/**
 * Get all PWA icon paths for a site
 * @param {Object} configuration - Site configuration object
 * @returns {Object} Object with all icon paths
 */
export const getSiteIcons = (configuration) => {
  const siteId = getSiteIdentifier(configuration);

  return {
    favicon: getIconPath(siteId, 'favicon.ico'),
    icon192: getIconPath(siteId, 'icon-192.png'),
    icon512: getIconPath(siteId, 'icon-512.png'),
    icon192Maskable: getIconPath(siteId, 'icon-192-maskable.png'),
    icon512Maskable: getIconPath(siteId, 'icon-512-maskable.png'),
    appleTouchIcon: getIconPath(siteId, 'apple-touch-icon.png'),
    appleTouchIcon152: getIconPath(siteId, 'apple-touch-icon-152.png'),
    appleTouchIcon167: getIconPath(siteId, 'apple-touch-icon-167.png'),
  };
};

/**
 * Get all PWA icon paths for a site with fallback checking (async version)
 * @param {Object} configuration - Site configuration object
 * @returns {Promise<Object>} Object with all icon paths (with fallbacks)
 */
export const getSiteIconsWithFallback = async (configuration) => {
  const siteId = getSiteIdentifier(configuration);

  return {
    favicon: await getIconPathWithFallback(siteId, 'favicon.ico'),
    icon192: await getIconPathWithFallback(siteId, 'icon-192.png'),
    icon512: await getIconPathWithFallback(siteId, 'icon-512.png'),
    icon192Maskable: await getIconPathWithFallback(
      siteId,
      'icon-192-maskable.png'
    ),
    icon512Maskable: await getIconPathWithFallback(
      siteId,
      'icon-512-maskable.png'
    ),
    appleTouchIcon: await getIconPathWithFallback(
      siteId,
      'apple-touch-icon.png'
    ),
    appleTouchIcon152: await getIconPathWithFallback(
      siteId,
      'apple-touch-icon-152.png'
    ),
    appleTouchIcon167: await getIconPathWithFallback(
      siteId,
      'apple-touch-icon-167.png'
    ),
  };
};

/**
 * Update the page's link tags with site-specific icons
 * Also updates apple-mobile-web-app-title meta tag
 * @param {Object} configuration - Site configuration object
 */
export const updatePageIcons = (configuration) => {
  const icons = getSiteIcons(configuration);
  // logDebug('[PWA Icons] Updating page icons:', icons);

  // Update apple-mobile-web-app-title meta tag (iOS "Add to Home Screen" name)
  if (configuration?.site_name) {
    let appleTitle = document.querySelector(
      'meta[name="apple-mobile-web-app-title"]'
    );
    if (!appleTitle) {
      appleTitle = document.createElement('meta');
      appleTitle.setAttribute('name', 'apple-mobile-web-app-title');
      document.head.appendChild(appleTitle);
    }
    appleTitle.setAttribute('content', configuration.site_name);
    // logDebug(
    //   `[PWA Icons] Set apple-mobile-web-app-title: ${configuration.site_name}`
    // );
  }

  // Update favicon
  const faviconLink = document.querySelector(
    'link[rel="icon"][type="image/x-icon"]'
  );
  if (faviconLink) {
    // logDebug(`[PWA Icons] Updating favicon: ${icons.favicon}`);
    faviconLink.href = icons.favicon;
  }

  // Update apple-touch-icons
  const appleTouchIcon = document.querySelector(
    'link[rel="apple-touch-icon"]:not([sizes])'
  );
  if (appleTouchIcon) {
    appleTouchIcon.href = icons.appleTouchIcon;
  }

  const appleTouchIcon152 = document.querySelector(
    'link[rel="apple-touch-icon"][sizes="152x152"]'
  );
  if (appleTouchIcon152) {
    appleTouchIcon152.href = icons.appleTouchIcon152;
  }

  const appleTouchIcon167 = document.querySelector(
    'link[rel="apple-touch-icon"][sizes="167x167"]'
  );
  if (appleTouchIcon167) {
    appleTouchIcon167.href = icons.appleTouchIcon167;
  }

  const appleTouchIcon180 = document.querySelector(
    'link[rel="apple-touch-icon"][sizes="180x180"]'
  );
  if (appleTouchIcon180) {
    appleTouchIcon180.href = icons.appleTouchIcon;
  }

  // Update standard icons
  const icon192 = document.querySelector('link[rel="icon"][sizes="192x192"]');
  if (icon192) {
    icon192.href = icons.icon192;
  }

  const icon512 = document.querySelector('link[rel="icon"][sizes="512x512"]');
  if (icon512) {
    icon512.href = icons.icon512;
  }
};

/**
 * Update the page's link tags with site-specific icons using hybrid fallback logic
 * Uses defaults immediately, then updates to site-specific icons asynchronously
 * @param {Object} configuration - Site configuration object
 */
export const updatePageIconsHybrid = async (configuration) => {
  const siteId = getSiteIdentifier(configuration);

  // Phase 1: Set default icons immediately (sync)
  const defaultIcons = getSiteIcons({}); // Empty config forces defaults
  updateIconElements(defaultIcons, 'default', configuration);

  // Phase 2: Check and update to site-specific icons (async)
  if (siteId) {
    try {
      const siteIcons = await getSiteIconsWithFallback(configuration);
      updateIconElements(siteIcons, 'site-specific', configuration);
    } catch (error) {
      logError('[PWA Icons] Phase 2 failed, keeping defaults:', error);
    }
  }
};

/**
 * Internal function to update DOM icon elements
 * @param {Object} icons - Icon paths object
 * @param {string} phase - Phase name for logging
 * @param {Object} configuration - Site configuration for title
 */
const updateIconElements = (icons, phase, configuration) => {
  // Update apple-mobile-web-app-title meta tag
  const appleTitle = document.querySelector(
    'meta[name="apple-mobile-web-app-title"]'
  );
  if (appleTitle && configuration?.site_name) {
    appleTitle.setAttribute('content', configuration.site_name);
  }

  // Add cache busting for all icons to force browser reload
  const cacheBuster = `?t=${Date.now()}`;

  // AGGRESSIVE FAVICON UPDATE - Update ALL favicon-like links
  const allIconLinks = document.querySelectorAll('link[rel*="icon"]');

  allIconLinks.forEach((link, index) => {
    const rel = link.getAttribute('rel');
    const type = link.getAttribute('type');
    const sizes = link.getAttribute('sizes');

    let newHref;

    // Match icon type to appropriate icon
    if (rel === 'icon' && type === 'image/x-icon') {
      newHref = icons.favicon + cacheBuster;
    } else if (rel === 'icon' && type === 'image/png' && sizes === '192x192') {
      newHref = icons.icon192 + cacheBuster;
    } else if (rel === 'icon' && type === 'image/png' && sizes === '512x512') {
      newHref = icons.icon512 + cacheBuster;
    } else if (rel === 'apple-touch-icon' && !sizes) {
      newHref = icons.appleTouchIcon + cacheBuster;
    } else if (rel === 'apple-touch-icon' && sizes === '152x152') {
      newHref = icons.appleTouchIcon152 + cacheBuster;
    } else if (rel === 'apple-touch-icon' && sizes === '167x167') {
      newHref = icons.appleTouchIcon167 + cacheBuster;
    } else if (rel === 'apple-touch-icon' && sizes === '180x180') {
      newHref = icons.appleTouchIcon + cacheBuster;
    } else {
      return;
    }

    // Update the href
    link.href = newHref;

    // Add error handling
    link.onerror = () => {
      logWarn(`[PWA Icons] ${phase}: Icon failed to load: ${newHref}`);
      if (!newHref.includes('/icons/default/')) {
        // Fallback to default version
        let defaultHref;
        if (rel === 'icon' && type === 'image/x-icon') {
          defaultHref = `/icons/default/favicon.ico${cacheBuster}`;
        } else if (
          rel === 'icon' &&
          type === 'image/png' &&
          sizes === '192x192'
        ) {
          defaultHref = `/icons/default/icon-192.png${cacheBuster}`;
        } else if (
          rel === 'icon' &&
          type === 'image/png' &&
          sizes === '512x512'
        ) {
          defaultHref = `/icons/default/icon-512.png${cacheBuster}`;
        } else if (rel === 'apple-touch-icon' && !sizes) {
          defaultHref = `/icons/default/apple-touch-icon.png${cacheBuster}`;
        } else if (rel === 'apple-touch-icon' && sizes === '152x152') {
          defaultHref = `/icons/default/apple-touch-icon-152.png${cacheBuster}`;
        } else if (rel === 'apple-touch-icon' && sizes === '167x167') {
          defaultHref = `/icons/default/apple-touch-icon-167.png${cacheBuster}`;
        } else if (rel === 'apple-touch-icon' && sizes === '180x180') {
          defaultHref = `/icons/default/apple-touch-icon.png${cacheBuster}`;
        }

        if (defaultHref) {
          link.href = defaultHref;
        }
      }
    };
  });

  // Force favicon reload by removing and re-adding ALL icon links
  setTimeout(() => {
    allIconLinks.forEach((link) => {
      if (link && link.parentNode) {
        const newLink = link.cloneNode(true);
        link.parentNode.replaceChild(newLink, link);
      } else {
        logWarn(
          `[PWA Icons] ${phase}: Cannot replace icon link - no parent node available`
        );
      }
    });
  }, 100);
};

/**
 * Generate manifest.json content for a specific site
 * @param {Object} configuration - Site configuration object
 * @returns {Object} Manifest object
 */
export const generateManifest = (configuration) => {
  const siteId = getSiteIdentifier(configuration);
  // CRITICAL: No defaults - wait for API to load site-specific data
  // This codebase serves 100+ different sites, never default to 'Downkount'
  const siteName = configuration?.site_name || '';
  const siteDescription = configuration?.site_description || '';

  // Use absolute URLs for manifest since it's served as a blob
  const origin = window.location.origin;
  const getAbsoluteIconPath = (filename) =>
    `${origin}${getIconPath(siteId, filename)}`;

  // Generate proper manifest name (e.g., "Lakers Time - The Countdown Continues...")
  const manifestName = siteName
    ? `${siteName} - The Countdown Continues...`
    : 'The Countdown Continues...';

  return {
    short_name: siteName,
    name: manifestName,
    description: siteDescription,
    icons: [
      {
        src: getAbsoluteIconPath('favicon.ico'),
        sizes: '32x32',
        type: 'image/x-icon',
      },
      {
        src: getAbsoluteIconPath('icon-192.png'),
        sizes: '192x192',
        type: 'image/png',
        purpose: 'any',
      },
      {
        src: getAbsoluteIconPath('icon-512.png'),
        sizes: '512x512',
        type: 'image/png',
        purpose: 'any',
      },
      {
        src: getAbsoluteIconPath('icon-192-maskable.png'),
        sizes: '192x192',
        type: 'image/png',
        purpose: 'maskable',
      },
      {
        src: getAbsoluteIconPath('icon-512-maskable.png'),
        sizes: '512x512',
        type: 'image/png',
        purpose: 'maskable',
      },
    ],
    start_url: `${origin}/`,
    scope: `${origin}/`,
    display: 'standalone',
    orientation: 'portrait-primary',
    theme_color: configuration?.primary_color || '#000000',
    background_color: '#ffffff',
    categories: ['sports', 'entertainment'],
    prefer_related_applications: false,
  };
};

/**
 * Update the manifest link to point to a dynamically generated manifest
 * @param {Object} configuration - Site configuration object
 */
export const updateManifest = (configuration) => {
  const manifest = generateManifest(configuration);
  const manifestJson = JSON.stringify(manifest);
  const blob = new Blob([manifestJson], { type: 'application/json' });
  const manifestURL = URL.createObjectURL(blob);

  const manifestLink = document.querySelector('link[rel="manifest"]');
  if (manifestLink) {
    // Revoke old URL if it was a blob
    if (manifestLink.href.startsWith('blob:')) {
      URL.revokeObjectURL(manifestLink.href);
    }
    manifestLink.href = manifestURL;
  }
};
